// Composable dropdown primitives — eliminates duplicated Tailwind across recap dropdowns
import { forwardRef, useEffect, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Search, ChevronDown, Check } from "lucide-react";
import { createPortal } from "react-dom";
import type { usePortalDropdown } from "./dropdown-hooks";

/** Container for the floating dropdown panel */
export const DropdownPanel = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-recap-portal
      className={cn(
        "bg-white border border-gray-200 rounded-md shadow-lg",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
});
DropdownPanel.displayName = "DropdownPanel";

/** Search section with optional icon and optional trailing adornment */
export const DropdownSearch = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & {
    hasIcon?: boolean;
    /** Right-aligned adornment (e.g. a loading spinner) overlaid on the input. */
    trailing?: ReactNode;
  }
>(({ hasIcon = true, trailing, className, ...props }, ref) => {
  return (
    <div className="p-1.5 border-b">
      <div className="relative">
        {hasIcon && (
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        )}
        <input
          ref={ref}
          className={cn(
            "w-full h-7 text-xs border border-gray-200 rounded bg-gray-50/80 focus:bg-white focus:border-blue-300 focus:ring-1 focus:ring-blue-100 focus:outline-none transition-colors",
            hasIcon ? "pl-8 pr-2" : "px-2",
            trailing && "pr-7",
            className,
          )}
          {...props}
        />
        {trailing && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">
            {trailing}
          </div>
        )}
      </div>
    </div>
  );
});
DropdownSearch.displayName = "DropdownSearch";

/** Scrollable list container */
export const DropdownList = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { maxHeight?: string }
>(({ className, maxHeight = "240px", children, style, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn("overflow-y-auto", className)}
      style={{ maxHeight, ...style }}
      {...props}
    >
      {children}
    </div>
  );
});
DropdownList.displayName = "DropdownList";

/** Individual item in the list */
export function DropdownItem({
  highlighted,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { highlighted?: boolean }) {
  return (
    <div
      className={cn(
        "flex items-center px-2 py-1.5 cursor-pointer text-xs transition-colors",
        highlighted ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/** Trigger button for dropdown */
export const DropdownTrigger = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { open?: boolean }
>(({ open, className, children, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "h-8 max-w-[200px] truncate rounded border px-2 text-sm flex items-center gap-1.5 transition-colors text-left",
        "border-gray-200 bg-gray-50/80 text-gray-700",
        open && "bg-white border-blue-500 ring-1 ring-blue-500 outline-none",
        !open && "hover:border-gray-300",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});
DropdownTrigger.displayName = "DropdownTrigger";

/** Chevron indicator */
export function DropdownChevron({
  open,
  className,
}: {
  open?: boolean;
  className?: string;
}) {
  return (
    <ChevronDown
      data-chevron
      className={cn(
        "h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform",
        open && "rotate-180 text-blue-500",
        className,
      )}
    />
  );
}

/** Empty state message */
export function DropdownEmpty({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("p-2 text-xs text-center text-gray-400", className)}>
      {children}
    </div>
  );
}

// ── Searchable dropdown content ─────────────────────────────────────

export interface SearchableDropdownItem {
  id: string | null;
  label: string;
  sublabel?: string;
}

export interface SearchableDropdownContentProps {
  /** Result of usePortalDropdown() */
  dd: ReturnType<typeof usePortalDropdown>;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  items: SearchableDropdownItem[];
  value: string | null;
  onChange: (id: string | null) => void;
  emptyMessage: string;
  searchPlaceholder?: string;
  hasSearchIcon?: boolean;
  maxHeight?: string;
  /** Optional content rendered between the search input and the list */
  headerSlot?: ReactNode;
  /** Override the default item rendering */
  renderItem?: (
    item: SearchableDropdownItem,
    index: number,
    highlighted: boolean,
  ) => ReactNode;
}

/**
 * Fully-assembled dropdown panel with search, scrollable list, and selection.
 * Portals itself to document.body when dd.open && dd.dropdownPos are truthy.
 *
 * Replaces the duplicated ~50-line JSX block in PartnerButton, LocationButton,
 * and EmployeeSelector.
 */
export function SearchableDropdownContent({
  dd,
  searchQuery,
  onSearchChange,
  items,
  value,
  onChange,
  emptyMessage,
  searchPlaceholder = "Search...",
  hasSearchIcon = true,
  maxHeight,
  headerSlot,
  renderItem,
}: SearchableDropdownContentProps) {
  // Keep the highlighted row visible when keyboard navigation moves it past
  // the scrollable list edge. Only scroll for keyboard-driven changes — mouse
  // hover already has the item in view and scrolling would fight the user.
  useEffect(() => {
    const wasKeyboard = dd.isKeyboardNavRef.current;
    dd.isKeyboardNavRef.current = false;
    if (!dd.open || !dd.dropdownPos || !wasKeyboard) return;
    const panel = dd.dropdownRef.current;
    if (!panel) return;
    const row = panel.querySelector<HTMLElement>(
      `[data-dropdown-index="${dd.highlightedIndex}"]`,
    );
    row?.scrollIntoView({ block: "nearest" });
  }, [
    dd.highlightedIndex,
    dd.open,
    dd.dropdownPos,
    dd.dropdownRef,
    dd.isKeyboardNavRef,
  ]);

  // When the search query or item list changes, reset scroll and highlight to
  // the top. We depend on searchQuery (not just itemCount) because a search can
  // swap rows without changing count.
  const itemCount = items.length;
  useEffect(() => {
    if (!dd.open) return;
    const panel = dd.dropdownRef.current;
    const scrollable = panel?.querySelector<HTMLElement>(".overflow-y-auto");
    if (scrollable) scrollable.scrollTop = 0;
    dd.setHighlightedIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, itemCount, dd.open, dd.dropdownRef, dd.setHighlightedIndex]);

  if (!dd.open || !dd.dropdownPos) return null;

  // List max height = panel max height minus search bar (~44px) and small buffer.
  // Reserve extra space when a headerSlot is rendered so its content doesn't
  // push items past the viewport edge.
  const headerReserveOffset = headerSlot ? 36 : 0;
  const listMaxHeightPx = Math.max(
    80,
    dd.dropdownPos.maxHeight - 52 - headerReserveOffset,
  );
  const resolvedListMaxHeight = maxHeight
    ? `min(${maxHeight}, ${listMaxHeightPx}px)`
    : `${listMaxHeightPx}px`;

  const panel = (
    <DropdownPanel
      ref={dd.dropdownRef}
      style={{
        position: "fixed",
        top: dd.dropdownPos.top,
        bottom: dd.dropdownPos.bottom,
        left: dd.dropdownPos.left,
        width: dd.dropdownPos.width,
        maxHeight: dd.dropdownPos.maxHeight,
        display: "flex",
        flexDirection: "column",
        zIndex: 9999,
      }}
      onKeyDown={dd.handleKeyDown}
    >
      <DropdownSearch
        ref={dd.searchInputRef}
        hasIcon={hasSearchIcon}
        placeholder={searchPlaceholder}
        value={searchQuery}
        onChange={(e) => {
          onSearchChange(e.target.value);
          dd.setHighlightedIndex(0);
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          dd.handleKeyDown(e);
        }}
      />
      {headerSlot}
      <DropdownList maxHeight={resolvedListMaxHeight}>
        {items.length === 0 ? (
          <DropdownEmpty>{emptyMessage}</DropdownEmpty>
        ) : (
          items.map((item, index) => {
            const highlighted = index === dd.highlightedIndex;

            if (renderItem) {
              return (
                <DropdownItem
                  key={item.id ?? "none"}
                  highlighted={highlighted}
                  data-dropdown-index={index}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange(item.id);
                    dd.setOpen(false);
                  }}
                  onMouseEnter={() => dd.setHighlightedIndex(index)}
                >
                  {renderItem(item, index, highlighted)}
                </DropdownItem>
              );
            }

            return (
              <DropdownItem
                key={item.id ?? "none"}
                highlighted={highlighted}
                data-dropdown-index={index}
                className={cn(
                  "justify-between text-sm",
                  item.id === null && "text-gray-400",
                )}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(item.id);
                  dd.setOpen(false);
                }}
                onMouseEnter={() => dd.setHighlightedIndex(index)}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{item.label}</div>
                  {item.sublabel && (
                    <div className="text-[10px] text-gray-400">
                      {item.sublabel}
                    </div>
                  )}
                </div>
                {value === item.id && (
                  <Check className="h-3 w-3 shrink-0 text-blue-500 ml-2" />
                )}
              </DropdownItem>
            );
          })
        )}
      </DropdownList>
    </DropdownPanel>
  );

  if (typeof window === "undefined") return null;
  return createPortal(panel, document.body);
}
